import { logout } from '@portal/actions/auth';
import {
  blockDevice,
  deleteDevice,
  getDeviceCount,
  getUserDevices,
  unlockDevice,
} from '@portal/actions/device';
import { myUsers } from '@portal/actions/user';
import AdvancedModal from '@portal/components/AdvancedModal/AdvancedModal';
import DeviceCard from '@portal/components/DeviceCard/DeviceCard';
import EmptyStateSection from '@portal/components/EmptyStateSection/EmptyStateSection';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import UserCard from '@portal/components/UserCard/UserCard';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';
import * as MessageService from '@portal/services/message';
import * as StorageService from '@portal/services/storage';
import { hasAccess } from '@portal/utils/hasAccess';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DesktopOutlined } from '@ant-design/icons';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import { useNavigation } from '@portal/hooks/useNavigation';
import './MyDevices.less';

interface IProps {
  isBlockedUsers?: boolean;
}

const MyDevices = ({ isBlockedUsers }: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    user: { myUsersList, roles },
    device: { myDevices, deviceCount },
  } = useReduxState();

  const [isVisible, setIsVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<models.MyUsers>();
  const [selectedDevice, setSelectedDevice] = useState<reducers.IMyDevice>();
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [actionType, setActionType] = useState<
    'disconnect' | 'block' | 'unblock' | null
  >(null);
  const [filteredUsers, setFilteredUsers] = useState(myUsersList);
  const [filteredDevices, setFilteredDevices] = useState(myDevices);
  const [userSearch, setUserSearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');

  useEffect(() => {
    setFilteredUsers(myUsersList);
  }, [myUsersList]);

  useEffect(() => {
    setFilteredDevices(myDevices);
  }, [myDevices]);

  const hasDeviceAccess = hasAccess(roles, [
    RolesType.MY_ACCOUNT,
    RolesType.DEVICE_MANAGER,
  ]);

  if (!hasDeviceAccess) {
    return (
      <div className="container">
        <PanelContentHeader
          pageTitle={translate('PAGES.MY_ACCOUNT.MY_DEVICES.TITLE_USERS')}
          pageDescription={translate(
            'PAGES.MY_ACCOUNT.MY_DEVICES.DESCRIPTION_USERS'
          )}
        />
        <EmptyStateSection
          icon={<DesktopOutlined />}
          title={translate('APPLICATION.ERRORS.UNAUTHORIZED')}
          description={translate('APPLICATION.ERRORS.UNAUTHORIZED')}
        />
      </div>
    );
  }

  useEffect(() => {
    if (
      hasAccess(roles, [
        RolesType.ACCOUNT_READ,
        RolesType.ACCOUNT_ADD_USER,
        RolesType.MANAGE_ACCOUNTS,
      ])
    ) {
      dispatch(myUsers());
      dispatch(getDeviceCount());
    }
  }, [dispatch, roles]);

  const handleModal = (user: models.MyUsers) => {
    if (deviceCount) {
      const available = deviceCount.maxDevices - deviceCount.actualDevices;
      if (available === 0) {
        MessageService.warn(
          translate('PAGES.MY_ACCOUNT.MY_DEVICES.NO_SLOTS_AVAILABLE_WARNING')
        );
      }
    }

    setSelectedUser(user);
    if (user.id) {
      dispatch(getUserDevices(user.id));
    }
    setShowDevicesModal(true);
  };

  const handleDeviceAction = (
    device: reducers.IMyDevice,
    action: 'disconnect' | 'block' | 'unblock'
  ) => {
    setSelectedDevice(device);
    setActionType(action);
    setIsVisible(true);
  };

  const handleClose = () => {
    setIsVisible(false);
    setShowDevicesModal(false);
    setSelectedDevice(undefined);
    setActionType(null);
  };

  const handleCloseConfirmationModal = () => {
    setIsVisible(false);
    setSelectedDevice(undefined);
    setActionType(null);
  };

  const handleDisconnectDevice = () => {
    if (selectedDevice?.id) {
      const currentDeviceId = StorageService.getDeviceId();
      const isCurrentDevice = selectedDevice.deviceId === currentDeviceId;

      dispatch(
        deleteDevice(selectedDevice.id as string, (err) => {
          if (!err) {
            MessageService.success(
              translate('PAGES.MY_ACCOUNT.MY_DEVICES.DEVICE_DISCONNECTED')
            );

            if (selectedUser?.id) {
              dispatch(getUserDevices(selectedUser.id));
            }

            dispatch(getDeviceCount());

            if (isCurrentDevice) {
              dispatch(logout());
            }
          } else {
            MessageService.error(err.message, { originalError: err });
          }
        })
      );
      handleCloseConfirmationModal();
    }
  };

  const handleBlockDevice = () => {
    if (selectedDevice?.id) {
      dispatch(
        blockDevice(selectedDevice.id as string, (err) => {
          if (!err) {
            MessageService.success(
              translate('PAGES.MY_ACCOUNT.MY_DEVICES.DEVICE_BLOCKED')
            );
            if (selectedUser?.id) {
              dispatch(getUserDevices(selectedUser.id));
            }

            dispatch(getDeviceCount());
          } else {
            MessageService.error(err.message, { originalError: err });
          }
        })
      );
      handleCloseConfirmationModal();
    }
  };

  const handleUnblockDevice = () => {
    if (selectedDevice?.id) {
      dispatch(
        unlockDevice(selectedDevice.id as string, (err) => {
          if (!err) {
            MessageService.success(
              translate('PAGES.MY_ACCOUNT.MY_DEVICES.DEVICE_UNBLOCKED')
            );
            if (selectedUser?.id) {
              dispatch(getUserDevices(selectedUser.id));
            }

            dispatch(getDeviceCount());
          } else {
            MessageService.error(err.message, { originalError: err });
          }
        })
      );
      handleCloseConfirmationModal();
    }
  };

  const handleConfirmModal = () => {
    if (actionType === 'disconnect') {
      handleDisconnectDevice();
    } else if (actionType === 'block') {
      handleBlockDevice();
    } else if (actionType === 'unblock') {
      handleUnblockDevice();
    }
  };

  const getHeaderTitle = () => {
    let text = translate('PAGES.MY_ACCOUNT.MY_DEVICES.TITLE_USERS');
    let description = translate(
      'PAGES.MY_ACCOUNT.MY_DEVICES.DESCRIPTION_USERS'
    );

    if (isBlockedUsers) {
      text = translate('PAGES.MY_ACCOUNT.MY_DEVICES.TITLE_BLOCKED_USERS');
      description = translate(
        'PAGES.MY_ACCOUNT.MY_DEVICES.DESCRIPTION_BLOCKED_USERS'
      );
    }

    return (
      <AdvancedPanelContentHeader
        testId="my-devices-header"
        title={text}
        description={description}
        goBackAction={() => navigate.back()}
      />
    );
  };

  const renderDeviceSlotInfo = () => {
    if (!deviceCount) return null;

    const { actualDevices, maxDevices } = deviceCount;
    const available = maxDevices - actualDevices;
    const isLowAvailability = available <= 2;
    const isNoAvailability = available === 0;

    const className = `device-slots-info ${
      isNoAvailability ? 'no-slots' : isLowAvailability ? 'low-slots' : ''
    }`;

    return (
      <div className={className}>
        <div className="device-slots-info__content">
          <span>
            {translate('PAGES.MY_ACCOUNT.MY_DEVICES.SLOTS_USED')}{' '}
            {actualDevices}/{maxDevices}
          </span>
        </div>
      </div>
    );
  };

  const handleUserSearch = (value: string) => {
    setUserSearch(value);

    const search = value.toLowerCase();

    const filtered = myUsersList.filter((user) =>
      user.name?.toLowerCase().includes(search)
    );

    setFilteredUsers(filtered);
  };

  const handleDeviceSearch = (value: string) => {
    setDeviceSearch(value);

    const search = value.toLowerCase();

    const filtered = myDevices?.filter((device) =>
      [device.deviceId, device.os, device.brand, device.login, device.userName]
        .filter(Boolean)
        .some((field) => field?.toLowerCase().includes(search))
    );

    setFilteredDevices(filtered ?? []);
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.MY_ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.MY_ACCOUNT.MY_DEVICES.TITLE_USERS'),
          },
        ]}
      />
      <div className="container-devices">
        {getHeaderTitle()}
        <AdvancedCard testId="device-slot-card">
          {renderDeviceSlotInfo()}
          <hr />
          <AdvancedInput
            value={userSearch}
            placeholder={translate(
              'PAGES.MY_ACCOUNT.MY_DEVICES.SEARCH_PLACEHOLDER'
            )}
            onChange={handleUserSearch}
          />
          <div className="multiaccess-page__cards-grid">
            {filteredUsers.length > 0 &&
              filteredUsers.map((user) => (
                <UserCard
                  key={user?.id}
                  user={user}
                  type="accepted"
                  roles={roles}
                  clickable
                  onClick={() => handleModal(user)}
                />
              ))}
          </div>
        </AdvancedCard>
        <AdvancedModal
          size="large"
          visible={showDevicesModal}
          title={`${translate('PAGES.MY_ACCOUNT.MY_DEVICES.USER_DEVICES')} - ${
            selectedUser?.name
          }`}
          description={translate(
            'PAGES.MY_ACCOUNT.MY_DEVICES.USER_DEVICES_DESCRIPTION'
          )}
          onClose={handleClose}
          actions={[
            {
              label: translate('PAGES.MY_ACCOUNT.MY_DEVICES.CLOSE'),
              type: 'cancel',
              onClick: handleClose,
            },
          ]}
        >
          <div className="devices-list">
            <AdvancedInput
              value={deviceSearch}
              placeholder={translate(
                'PAGES.MY_ACCOUNT.MY_DEVICES.SEARCH_PLACEHOLDER'
              )}
              onChange={handleDeviceSearch}
            />
            {filteredDevices && filteredDevices.length > 0 ? (
              filteredDevices.map((device) => (
                <div key={device?.id} className="device-item">
                  <DeviceCard
                    deviceId={device?.deviceId || ''}
                    lastAccess={device?.lastAccess || ''}
                    os={device?.os || ''}
                    brand={device?.brand || ''}
                    login={device?.login}
                    userName={device?.userName}
                    status={device?.status}
                    onPress={() => handleDeviceAction(device, 'disconnect')}
                    onBlock={() => handleDeviceAction(device, 'block')}
                    onUnblock={() => handleDeviceAction(device, 'unblock')}
                  />
                </div>
              ))
            ) : (
              <EmptyStateSection
                icon={<DesktopOutlined />}
                title={translate(
                  'PAGES.MY_ACCOUNT.MY_DEVICES.EMPTY_STATE.TITLE'
                )}
                description={translate(
                  'PAGES.MY_ACCOUNT.MY_DEVICES.EMPTY_STATE.DESCRIPTION'
                )}
                className="empty-state-section--modal"
              />
            )}
          </div>
        </AdvancedModal>
        <AdvancedModal
          size="small"
          visible={isVisible}
          onClose={handleCloseConfirmationModal}
          title={
            actionType === 'disconnect'
              ? translate('PAGES.MY_ACCOUNT.MY_DEVICES.MODAL.DISCONNECT_DEVICE')
              : actionType === 'block'
              ? translate('PAGES.MY_ACCOUNT.MY_DEVICES.MODAL.BLOCK_DEVICE')
              : translate('PAGES.MY_ACCOUNT.MY_DEVICES.MODAL.UNBLOCK_DEVICE')
          }
          actions={[
            {
              label: translate('PAGES.MY_ACCOUNT.MY_DEVICES.CANCEL'),
              type: 'cancel',
              onClick: handleCloseConfirmationModal,
            },
            {
              label:
                actionType === 'disconnect'
                  ? translate('PAGES.MY_ACCOUNT.MY_DEVICES.DISCONNECT')
                  : actionType === 'block'
                  ? translate('PAGES.MY_ACCOUNT.MY_DEVICES.BLOCK')
                  : translate('PAGES.MY_ACCOUNT.MY_DEVICES.UNBLOCK'),
              type: 'ok',
              onClick: handleConfirmModal,
            },
          ]}
        >
          <div>
            {actionType === 'disconnect' && (
              <ThemedText variant="body">
                {translate(
                  'PAGES.MY_ACCOUNT.MY_DEVICES.MODAL.DISCONNECT_DEVICE_DESCRIPTION'
                )}
              </ThemedText>
            )}
            {actionType === 'block' && (
              <ThemedText variant="body">
                {translate(
                  'PAGES.MY_ACCOUNT.MY_DEVICES.MODAL.BLOCK_DEVICE_DESCRIPTION'
                )}
              </ThemedText>
            )}
            {actionType === 'unblock' && (
              <ThemedText variant="body">
                {translate(
                  'PAGES.MY_ACCOUNT.MY_DEVICES.MODAL.UNBLOCK_DEVICE_DESCRIPTION'
                )}
              </ThemedText>
            )}
          </div>
        </AdvancedModal>
      </div>
    </>
  );
};

export default MyDevices;
